import {
	Alert,
	AlertColor,
	Backdrop,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
} from '@mui/material';
import { Button, Menu } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import makeStyles from '@mui/styles/makeStyles/makeStyles';
import { pageSizeOptions } from 'constants/constants';
import { FilterGrid } from 'features/common/components/FilterGrid/FilterGrid';
import {
	convertFiltersToRecord,
	createSortHandler,
	getEnvelopedListPageTotal,
	getSortableFields,
} from 'features/common/helpers';
import {
	FilterModel,
	FilterType,
	IHeadCell,
	ISort,
	OperatorFilterEnum,
	SelectFilterModelSimple,
} from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PageNoResult } from '../../../common/components/PageNoResults';
import { branchOfficeTypeWithNameList, IBranchOfficeList } from '../../types';
import { getBranchOfficeList } from '../../services';
import routes from '../../../../constants/routes';
import { NEW_NOT_PHYSICAL, NEW_PHYSICAL } from '../../../../pages/BranchOffice/detail';
import EditIcon from '@mui/icons-material/Edit';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
	tableHeader: {
		backgroundColor: theme.palette.primary.main,
		color: 'white',
	},
	paginationHolder: {
		marginTop: theme.spacing(1),
		padding: 10,
		justifyContent: 'center',
		alignItems: 'center',
		display: 'flex',
	},
	statusChip: {
		fontWeight: 'bold',
	},
}));

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

const headerCells: IHeadCell[] = [
	{
		field: 'id',
		sortable: true,
		headerName: 'ID',
	},
	{
		field: 'name',
		sortable: true,
		headerName: 'Nombre',
	},
	{
		field: 'branchOfficeType',
		sortable: false,
		headerName: 'Tipo',
	},

	{
		field: 'phoneNumber',
		sortable: true,
		headerName: 'Teléfono',
	},

	{
		field: 'email',
		sortable: true,
		headerName: 'Email',
	},
	{
		field: 'active',
		sortable: true,
		headerName: 'Estado',
	},
	{
		field: '-',
		sortable: false,
		headerName: 'Acciones',
	},
];

const filters = [
	{ label: 'ID', type: FilterType.NUMERIC, key: 'id' },
	{ label: 'Nombre', type: FilterType.STRING, key: 'name' },
	new SelectFilterModelSimple('Tipo', 'branchOfficeType', branchOfficeTypeWithNameList),
	{ label: 'Teléfono', type: FilterType.STRING, key: 'phoneNumber' },

	{ label: 'Email', type: FilterType.STRING, key: 'email' },
	{
		label: 'Activo',
		type: FilterType.BOOLEAN,
		key: 'active',
		operator: OperatorFilterEnum.EQUALS,
	},
];

export const BranchOfficeList = (): JSX.Element => {
	const classes = useStyles();
	const history = useHistory();

	const [loading, setLoading] = useState<boolean>(false);
	const [currentSize, setCurrentSize] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [branchOfficeList, setBranchOfficeList] = useState<IBranchOfficeList[] | null>(null);
	const [sortFields, setSortFields] = useState<ISort[]>([{ field: 'name', order: 'desc' }]);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);

	const [anchorElNew, setAnchorElNew] = useState<null | HTMLElement>(null);
	const openNewMenu = Boolean(anchorElNew);

	const handleOpenNewMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorElNew(event.currentTarget);
	};

	const handleCloseNewMenu = () => setAnchorElNew(null);

	const handleCreatePhysical = () => {
		handleCloseNewMenu();
		history.push(`${routes.branchOffices.list}/${NEW_PHYSICAL}`);
	};

	const handleCreateNotPhysical = () => {
		handleCloseNewMenu();
		history.push(`${routes.branchOffices.list}/${NEW_NOT_PHYSICAL}`);
	};

	const loadBranchOfficeList = async () => {
		try {
			setLoading(true);
			const envelopedBranchOfficeList = (
				await getBranchOfficeList(currentPage - 1, currentSize, sortFields, convertFiltersToRecord(filterApplied))
			).data;
			setPageTotal(getEnvelopedListPageTotal(envelopedBranchOfficeList));
			setBranchOfficeList(envelopedBranchOfficeList.data);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado de sucursales'),
			});
			setLoading(false);
		}
	};

	useEffect(() => {
		loadBranchOfficeList();
	}, [filterApplied, sortFields, currentPage, currentSize]);

	const handleApplyFilters = (filters: FilterModel[]) => setFilterApplied([...filters]);

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const sortableFields = getSortableFields(headerCells);

	const transformValue = (key: string, value: any, row: IBranchOfficeList) => {
		switch (key) {
			case 'branchOfficeType':
				return branchOfficeTypeWithNameList.find((x) => x.id === row.branchOfficeType)?.name || 'UNKNOWN';
			case 'phoneNumber':
				return (row.phoneNumber = row.phoneNumber || '-');
			case 'email':
				return (row.email = row.email || '-');

			default:
				return value;
		}
	};

	const colSpan = headerCells.length;

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid container gap={1} paddingY={5}>
				<Grid container spacing={2}>
					<Grid item xs={10}>
						<FilterGrid filters={filters} handleAppliedFilters={handleApplyFilters} preAppliedFilters={filterApplied} />
					</Grid>

					<Grid item xs={2} display="flex" justifyContent="flex-end" alignItems="flex-start">
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							endIcon={<ArrowDropDownIcon />}
							onClick={handleOpenNewMenu}
							aria-controls={openNewMenu ? 'new-branchoffice-menu' : undefined}
							aria-haspopup="true"
							aria-expanded={openNewMenu ? 'true' : undefined}
						>
							Adicionar
						</Button>

						<Menu
							id="new-branchoffice-menu"
							anchorEl={anchorElNew}
							open={openNewMenu}
							onClose={handleCloseNewMenu}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						>
							<MenuItem onClick={handleCreatePhysical}>Sucursal física</MenuItem>
							<MenuItem onClick={handleCreateNotPhysical}>Sucursal no física</MenuItem>
						</Menu>
					</Grid>
				</Grid>
				<TableContainer component="main" sx={{ maxWidth: 'xl' }}>
					<div>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									{headerCells.map((cell) => {
										return cell.sortable ? (
											<TableCell
												key={cell.field}
												align="center"
												className={classes.tableHeader}
												sortDirection={
													sortFields.find((x) => x.field === cell.field)
														? sortFields.find((x) => x.field === cell.field)?.order
														: false
												}
											>
												<TableSortLabel
													active={cell.sortable && sortFields.find((x) => x.field === cell.field) !== undefined}
													sx={{
														color: 'white !important',
														'&:hover': {
															color: 'white !important',
														},
													}}
													direction={
														sortFields.find((x) => x.field === cell.field)
															? sortFields.find((x) => x.field === cell.field)?.order
															: 'asc'
													}
													onClick={createSortHandler(cell.field, sortFields, sortableFields, setSortFields)}
												>
													{cell.headerName}
												</TableSortLabel>
											</TableCell>
										) : (
											<TableCell className={classes.tableHeader} key={cell.field} align="center">
												{cell.headerName}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{!loading && branchOfficeList?.length === 0 ? (
									<TableRow>
										<TableCell colSpan={colSpan}>
											<PageNoResult />
										</TableCell>
									</TableRow>
								) : (
									branchOfficeList?.map((branchOfficeItem) => (
										<TableRow
											key={branchOfficeItem.id}
											hover
											onDoubleClick={() => history.push(`${routes.branchOffices.list}/${branchOfficeItem.id}`)}
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell align="center" component="p">
												{branchOfficeItem.id}
											</TableCell>
											<TableCell align="center" component="p">
												{branchOfficeItem.name}
											</TableCell>
											<TableCell align="center" component="p">
												{transformValue('branchOfficeType', branchOfficeItem.branchOfficeType, branchOfficeItem)}
											</TableCell>
											<TableCell align="center" component="p">
												{transformValue('phoneNumber', branchOfficeItem.phoneNumber, branchOfficeItem)}
											</TableCell>
											<TableCell align="center" component="p">
												{transformValue('email', branchOfficeItem.email, branchOfficeItem)}
											</TableCell>
											<TableCell align="center" component="p">
												<Chip
													label={branchOfficeItem.active ? 'Activo' : 'Inactivo'}
													color={branchOfficeItem.active ? 'success' : 'error'}
													size="small"
													className={classes.statusChip}
												/>
											</TableCell>
											<TableCell align="center" component="p">
												<Tooltip title="Editar">
													<IconButton
														aria-label="Editar sucursal"
														size="small"
														color="primary"
														onClick={(e) => {
															e.stopPropagation(); // evita disparar el onDoubleClick de la fila
															history.push(`${routes.branchOffices.list}/${branchOfficeItem.id}`);
														}}
													>
														<EditIcon fontSize="inherit" />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</TableContainer>
				<Grid item xs={12}>
					<Grid container justifyContent="center" flexDirection="row" padding={2}>
						<Grid item xs={12} md={1} textAlign="center">
							<Select value={currentSize} onChange={handleRowsPerPageChange}>
								{pageSizeOptions.map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12} md={6} marginTop={1}>
							<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
